import { __awaiter } from "tslib";
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from '@shared/routing';
import { SeatType, TravellerFieldEnum, useCheckinSaveOrderServicesMutation, useGetCheckinOrderQuery } from '@websky/graphql';
import { getCheckinOrderId, getCheckinHasStarted, hasMealServices, hasBaggageServices, getCheckinNotStarted, getCheckinIsBlockedForPayment, hasUpgradeServices, getIsCheckinCompletedOrPreseating, hasExtrasStepServices, hasInsuranceSeparateStep } from './store/order/selectors';
import { fillCheckinOrder } from './store/order/actions';
import { CheckinStep } from './types';
import { getUserValue } from '../../utils';
import { useIataCode } from '../../hooks';
import { groupSeatsBySegment } from './components/Checkin/Order/utils';
import { hasRestrictions as hasRestrictionsSelector } from '@entities/restrictions';
const PASSENGERS_PATHNAME_PATTERN = /^\/(\d+)\/?$/;
export const getTravellerRetryLink = (traveller) => {
    var _a, _b;
    const lastName = getUserValue(traveller, TravellerFieldEnum.LastName);
    const ticket = (_b = (_a = traveller.tickets) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.number;
    return `/search/${ticket}/${lastName}`;
};
/**
 * Проверяет наличие сегмента шага в pathname (например, `/seats` или `/seats/confirm`).
 */
const hasStepSegment = (pathname, step) => {
    return new RegExp(`/${step}(?:/|$)`).test(pathname);
};
const defaultSteps = [
    CheckinStep.Passengers,
    CheckinStep.Upgrade,
    CheckinStep.Baggage,
    CheckinStep.Meal,
    CheckinStep.Seats,
    CheckinStep.Insurance,
    CheckinStep.Extras,
    CheckinStep.Restrictions,
    CheckinStep.Payment
];
const y7Steps = [
    CheckinStep.Passengers,
    CheckinStep.Upgrade,
    CheckinStep.Insurance,
    CheckinStep.Extras,
    CheckinStep.Baggage,
    CheckinStep.Meal,
    CheckinStep.Seats,
    CheckinStep.Payment
];
export const useSteps = () => {
    const orderId = useSelector(getCheckinOrderId);
    const checkinHasStarted = useSelector(getCheckinHasStarted);
    const checkinIsBlockedForPayment = useSelector(getCheckinIsBlockedForPayment);
    const hasMeals = useSelector(hasMealServices);
    const hasBaggage = useSelector(hasBaggageServices);
    const hasRestrictions = useSelector(hasRestrictionsSelector);
    const hasExtrasServices = useSelector(hasExtrasStepServices);
    const hasUpgrade = useSelector(hasUpgradeServices);
    const insuranceAsSeparateStep = useSelector(hasInsuranceSeparateStep);
    const isCheckinCompletedOrPreseating = useSelector(getIsCheckinCompletedOrPreseating);
    const history = useHistory();
    const iataCode = useIataCode();
    const steps = iataCode === 'Y7' ? y7Steps : defaultSteps;
    const availableSteps = steps.filter(step => {
        switch (step) {
            case CheckinStep.Upgrade:
                return hasUpgrade;
            case CheckinStep.Baggage:
                return hasBaggage;
            case CheckinStep.Restrictions:
                return hasRestrictions;
            case CheckinStep.Meal:
                return hasMeals;
            case CheckinStep.Extras:
                return hasExtrasServices;
            case CheckinStep.Insurance:
                return insuranceAsSeparateStep;
            default:
                return true;
        }
    });
    const result = {
        setStep: (step, searchParams) => {
            if (searchParams) {
                history.push({ pathname: `/${orderId}/${step}`, search: searchParams });
            }
            else {
                history.push(`/${orderId}/${step}`);
            }
        },
        /**
         * ignoreBlockedForPayment нужен для навигации сразу после снятия блокировки заказа
         * (см. useUnlockOrder), когда checkinIsBlockedForPayment в замыкании ещё не успел
         * обновиться до актуального значения из стора
         */
        getNextStepEnum: (currentStep, ignoreBlockedForPayment = false) => {
            if (currentStep === CheckinStep.Passengers && isCheckinCompletedOrPreseating) {
                return CheckinStep.Passengers;
            }
            else if (checkinIsBlockedForPayment && !ignoreBlockedForPayment) {
                return CheckinStep.Payment;
            }
            else {
                const stepsList = checkinHasStarted ? availableSteps : steps;
                const currentStepIndex = Math.max(stepsList.indexOf(currentStep), 0);
                if (currentStepIndex === stepsList.length - 1) {
                    return currentStep;
                }
                else {
                    return stepsList[currentStepIndex + 1];
                }
            }
        },
        getNextStep: (currentStep, ignoreBlockedForPayment = false) => {
            return `/${orderId}/${result.getNextStepEnum(currentStep, ignoreBlockedForPayment)}`;
        },
        goToNextStep: (currentStep, replace = false, ignoreBlockedForPayment = false) => {
            const newURL = result.getNextStep(currentStep, ignoreBlockedForPayment);
            if (replace) {
                history.replace(newURL);
            }
            else {
                history.push(newURL);
            }
        },
        getPreviousStepEnum: (currentStep) => {
            if (checkinHasStarted) {
                const currentStepIndex = availableSteps.indexOf(currentStep);
                if (currentStepIndex === 0) {
                    return currentStep;
                }
                else {
                    return availableSteps[availableSteps.indexOf(currentStep) - 1];
                }
            }
            return currentStep;
        },
        previousStep: (currentStep, replace = false) => {
            const newURL = `/${orderId}/${result.getPreviousStepEnum(currentStep)}`;
            if (replace) {
                history.replace(newURL);
            }
            else {
                history.push(newURL);
            }
        },
        getStepFromPathname: (pathname) => {
            if (PASSENGERS_PATHNAME_PATTERN.test(pathname)) {
                return CheckinStep.Passengers;
            }
            for (const step of availableSteps) {
                if (step !== CheckinStep.Passengers && hasStepSegment(pathname, step)) {
                    return step;
                }
            }
            return undefined;
        },
        availableSteps
    };
    return result;
};
export const useServiceRequest = () => {
    const dispatch = useDispatch();
    const orderId = useSelector(getCheckinOrderId);
    const [save] = useCheckinSaveOrderServicesMutation();
    const request = (services) => __awaiter(void 0, void 0, void 0, function* () {
        const { data, errors } = yield save({
            variables: {
                params: {
                    id: orderId,
                    services
                }
            }
        });
        if (errors) {
            throw errors;
        }
        else if (data && data.CheckinSaveOrderServices) {
            dispatch(fillCheckinOrder(data.CheckinSaveOrderServices));
            return data.CheckinSaveOrderServices;
        }
    });
    /**
     * This is a hack to support old RunServiceRequest thing.
     * Do not use this intentionally.
     *
     * @deprecated
     */
    const runServiceRequest = (passengers) => __awaiter(void 0, void 0, void 0, function* () {
        return yield request(passengers.map((passenger) => ({
            travellerId: passenger.passengerId,
            seats: passenger.seats
                ? passenger.seats.map((seat) => ({
                    segmentId: seat.segment.id,
                    letter: seat.letter,
                    row: seat.row
                }))
                : [],
            gdsServices: {
                setServices: passenger.setServices
            }
        })));
    });
    return {
        runServiceRequest
    };
};
export const getRelatedSegments = (groups, segmentId = null) => {
    const result = new Set();
    if (segmentId === null) {
        groups.forEach(group => {
            result.add(group[0]);
        });
    }
    else {
        groups.some(group => {
            if (group.includes(segmentId)) {
                group.forEach(segmentId => result.add(segmentId));
                return true;
            }
            return false;
        });
    }
    return result;
};
export const createServicesForRequest = (selectedServices, baggageServices) => {
    const passengers = new Map();
    for (const passengerId in selectedServices) {
        if (selectedServices.hasOwnProperty(passengerId)) {
            const passengerServices = selectedServices[passengerId];
            let ignoreServiceId = null;
            for (const serviceId in passengerServices) {
                if (passengerServices.hasOwnProperty(serviceId)) {
                    const servicesBySegments = passengerServices[serviceId];
                    const relatedService = baggageServices === null || baggageServices === void 0 ? void 0 : baggageServices.find(baggageService => baggageService.id === serviceId);
                    for (const segmentId in servicesBySegments) {
                        if (servicesBySegments.hasOwnProperty(segmentId)) {
                            const count = servicesBySegments[segmentId];
                            if (ignoreServiceId === serviceId) {
                                ignoreServiceId = null;
                                continue;
                            }
                            if (!passengers.has(passengerId)) {
                                passengers.set(passengerId, {
                                    travellerId: passengerId,
                                    gdsServices: {
                                        setServices: []
                                    }
                                });
                            }
                            const passenger = passengers.get(passengerId);
                            // Sometimes service should be added to a multiple segments at the same time.
                            // This happens when service is tightly bound to a whole trip itself (to a "leg" in terms of Nemo),
                            // rather than to a particular segment. in this case, just ignore next segment in selectedServices
                            if (relatedService === null || relatedService === void 0 ? void 0 : relatedService.allowedSegments.some(allowedSegment => allowedSegment.includes(segmentId))) {
                                ignoreServiceId = serviceId;
                                passenger.gdsServices.setServices.push({
                                    serviceId,
                                    count,
                                    segmentIds: [
                                        ...relatedService === null || relatedService === void 0 ? void 0 : relatedService.allowedSegments.find(allowedSegment => allowedSegment.includes(segmentId))
                                    ]
                                });
                            }
                            else {
                                passenger.gdsServices.setServices.push({
                                    serviceId,
                                    count,
                                    segmentIds: [segmentId]
                                });
                            }
                        }
                    }
                }
            }
        }
    }
    return [...passengers.values()];
};
export const useCheckinAvailabilityInfo = (pollInterval = 60000) => {
    var _a, _b;
    const [showExpireModal, setShowExpireModal] = useState(false);
    const isCheckinNew = useSelector(getCheckinNotStarted);
    const isCheckinStarted = useSelector(getCheckinHasStarted);
    const checkinOrderId = useSelector(getCheckinOrderId);
    const { startPolling, stopPolling, loading, data } = useGetCheckinOrderQuery({
        variables: {
            id: checkinOrderId
        },
        skip: !checkinOrderId
    });
    useEffect(() => {
        if (checkinOrderId) {
            startPolling(pollInterval);
        }
        return stopPolling;
    }, [checkinOrderId]);
    const isAvailable = (_b = (_a = data === null || data === void 0 ? void 0 : data.CheckinOrder) === null || _a === void 0 ? void 0 : _a.segments) === null || _b === void 0 ? void 0 : _b.some(segment => { var _a; return (_a = segment.checkinInfo) === null || _a === void 0 ? void 0 : _a.checkinAvailable; });
    const open = useCallback(() => setShowExpireModal(true), []);
    const close = useCallback(() => setShowExpireModal(false), []);
    useEffect(() => {
        if ((isCheckinStarted || isCheckinNew) && checkinOrderId && !loading && !isAvailable) {
            setShowExpireModal(true);
            stopPolling();
        }
    }, [isAvailable]);
    return { loading, isAvailable, showExpireModal, open, close, stopPolling };
};
export const getMissingThroughFlights = (segments, selectedSegments, getFlightName) => {
    const missingSegments = [];
    segments.forEach(segment => {
        if (!selectedSegments.has(segment.id) || !segment.checkinWithSegments.length) {
            return null;
        }
        const requiredThroughSegments = segment.checkinWithSegments
            .map(throughSegment => {
            if (throughSegment.id !== segment.id && !selectedSegments.has(throughSegment.id)) {
                const throughSegmentInfo = segments.find(s => s.id === throughSegment.id);
                return getFlightName(throughSegmentInfo);
            }
            return null;
        })
            .filter(Boolean);
        if (requiredThroughSegments.length) {
            missingSegments.push({
                mainSegment: getFlightName(segment),
                missingSegments: requiredThroughSegments
            });
        }
    });
    return missingSegments;
};
export const checkIfAllSeatsConfirmed = (travellers, segmentsLength) => {
    return travellers
        .filter(p => p.type !== 'INF')
        .every(p => {
        var _a;
        const seats = (_a = p.services) === null || _a === void 0 ? void 0 : _a.seats;
        return (seats === null || seats === void 0 ? void 0 : seats.every(s => s.isConfirmed)) && seats.length >= segmentsLength;
    });
};
export const prepareServicesPayload = (travellers, servicesMap) => {
    return travellers.map(passenger => {
        var _a, _b;
        const seats = ((_a = passenger === null || passenger === void 0 ? void 0 : passenger.services) === null || _a === void 0 ? void 0 : _a.seats) || [];
        const seatsBySegments = groupSeatsBySegment(seats);
        return {
            travellerId: passenger.id,
            gdsServices: (_b = servicesMap.get(passenger.id)) !== null && _b !== void 0 ? _b : {},
            seats: flattenSeatsWithType(seatsBySegments)
        };
    });
};
export const flattenSeatsWithType = (seatsBySegments) => {
    var _a, _b;
    const result = [];
    for (const segmentSeats of seatsBySegments) {
        const lastIndex = segmentSeats.length - 1;
        for (let i = 0; i <= lastIndex; i++) {
            const seat = segmentSeats[i];
            result.push({
                segmentId: (_b = (_a = seat.segment) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null,
                letter: seat.letter,
                row: seat.row,
                type: segmentSeats.length > 1 ? (i === lastIndex ? SeatType.extraSeat : SeatType.common) : null
            });
        }
    }
    return result;
};
export const validateOrderSeats = (travellers, segments) => {
    const requiredSegmentsCount = segments.filter(s => s.availableSeatsCount > 0).length;
    return travellers.every(t => { var _a, _b, _c; return t.type === 'INF' || ((_c = (_b = (_a = t.services) === null || _a === void 0 ? void 0 : _a.seats) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) >= requiredSegmentsCount; });
};
